'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import type { Table } from '@/lib/table-features';
import { Loader2, Trash2, Shield } from 'lucide-react';

import { useHashDialog } from '@/hooks/use-hash-dialog';
import { cn } from '@/lib/utils';
import type { BulkPayload } from '@/types/bulk-action';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { ROLES_HASH } from '../constants';
import {
  useBulkDeleteRolesMutation,
  useBulkAssignPermissionsMutation
} from '../services/roles.mutations';
import { permissionQueries } from '../services/roles.queries';
import { groupPermissions } from '../mappers/role.mapper';
import { isHiddenPermission } from './role-permissions-matrix-config';
import { useHiddenPermissionGroups } from './use-hidden-permission-groups';
import type { Role } from '../types';

interface RolesTableActionBarProps {
  table: Table<Role>;
  getBulkPayload: () => BulkPayload;
  onComplete: () => void;
  isAllPagesSelected?: boolean;
  totalCount?: number;
}

export function RolesTableActionBar({
  table,
  getBulkPayload,
  onComplete,
  isAllPagesSelected,
  totalCount
}: RolesTableActionBarProps) {
  const t = useTranslations('roles');
  // Overlay open-state lives in the URL hash (#bulk-delete, #bulk-assign) so
  // the action-bar dialogs are deep-linkable and close on browser Back.
  const { open: deleteOpen, onOpenChange: toggleDelete } = useHashDialog(
    ROLES_HASH.bulkDelete
  );
  const { open: assignOpen, onOpenChange: toggleAssign } = useHashDialog(
    ROLES_HASH.bulkAssign
  );
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);

  const bulkDeleteMutation = useBulkDeleteRolesMutation();
  const bulkAssignMutation = useBulkAssignPermissionsMutation();
  const { data: permissionsData } = useQuery(permissionQueries.list());
  const hiddenGroups = useHiddenPermissionGroups();

  const selectedRows = table.getFilteredSelectedRowModel().rows;
  const selectedRoles = selectedRows.map((row) => row.original);
  const hasUsersInSelection = selectedRoles.some(
    (role) => role.users_count > 0
  );
  const displayCount =
    isAllPagesSelected && totalCount ? totalCount : selectedRows.length;

  // Retired groups are dropped here too, so the bulk dialog can't hand out
  // a permission the single-role editor no longer offers.
  const permissionGroups = permissionsData?.data
    ? groupPermissions(
        (
          permissionsData.data as {
            id: number;
            name: string;
            guard_name: string;
          }[]
        ).filter((perm) => !isHiddenPermission(perm.name, hiddenGroups))
      )
    : [];

  async function handleBulkDelete() {
    const payload = getBulkPayload();
    try {
      if (payload.mode === 'all') {
        await bulkDeleteMutation.mutateAsync({
          all: true,
          filters: payload.filters,
          force: hasUsersInSelection
        });
      } else {
        await bulkDeleteMutation.mutateAsync({
          ids: payload.ids,
          force: hasUsersInSelection
        });
      }
      onComplete();
    } catch {
      // handled by global MutationCache
    }
    toggleDelete(false);
  }

  async function handleBulkAssignPermissions() {
    if (selectedPermissions.length === 0) return;
    const payload = getBulkPayload();
    try {
      if (payload.mode === 'all') {
        await bulkAssignMutation.mutateAsync({
          all: true,
          filters: payload.filters,
          permissions: selectedPermissions
        });
      } else {
        await bulkAssignMutation.mutateAsync({
          ids: payload.ids,
          permissions: selectedPermissions
        });
      }
      onComplete();
    } catch {
      // handled by global MutationCache
    }
    toggleAssign(false);
    setSelectedPermissions([]);
  }

  function togglePermission(name: string) {
    setSelectedPermissions((prev) =>
      prev.includes(name) ? prev.filter((p) => p !== name) : [...prev, name]
    );
  }

  return (
    <>
      <div className='bg-muted flex items-center gap-3 rounded-lg px-4 py-2'>
        <span className='text-muted-foreground text-sm font-medium'>
          {t('selectedCount', { count: displayCount })}
        </span>
        <div className='flex items-center gap-2'>
          <Button
            variant='destructive'
            size='sm'
            onClick={() => toggleDelete(true)}
          >
            <Trash2 className='me-1.5 h-3.5 w-3.5' />
            {t('bulkDelete')}
          </Button>
          <Button
            variant='outline'
            size='sm'
            onClick={() => toggleAssign(true)}
          >
            <Shield className='me-1.5 h-3.5 w-3.5' />
            {t('bulkAssignPermissions')}
          </Button>
        </div>
      </div>

      {/* Bulk delete confirmation — #bulk-delete */}
      <AlertDialog open={deleteOpen} onOpenChange={toggleDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('bulkDeleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {hasUsersInSelection ? (
                <span className='text-destructive font-medium'>
                  {t('bulkDeleteForceWarning', {
                    count: displayCount
                  })}
                </span>
              ) : (
                t('bulkDeleteConfirm', { count: displayCount })
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              className={cn(
                'bg-destructive text-destructive-foreground hover:bg-destructive/90'
              )}
            >
              {bulkDeleteMutation.isPending && (
                <Loader2 className='me-2 h-4 w-4 animate-spin' />
              )}
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk assign permissions dialog — #bulk-assign */}
      <Dialog open={assignOpen} onOpenChange={toggleAssign}>
        <DialogContent className='max-w-lg'>
          <DialogHeader>
            <DialogTitle>{t('bulkAssignPermissions')}</DialogTitle>
            <DialogDescription>
              {t('bulkAssignDescription', { count: displayCount })}
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className='max-h-[400px]'>
            <div className='space-y-4 pe-4'>
              {permissionGroups.map((group) => (
                <div key={group.group} className='space-y-2'>
                  <h4 className='text-sm font-medium capitalize'>
                    {group.group}
                  </h4>
                  <div className='grid grid-cols-2 gap-2'>
                    {group.permissions.map((perm) => (
                      <div
                        key={perm.id}
                        className='flex items-center space-x-2'
                      >
                        <Checkbox
                          id={`bulk-perm-${perm.id}`}
                          checked={selectedPermissions.includes(perm.name)}
                          onCheckedChange={() => togglePermission(perm.name)}
                        />
                        <Label
                          htmlFor={`bulk-perm-${perm.id}`}
                          className='text-sm'
                        >
                          {perm.name}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => {
                toggleAssign(false);
                setSelectedPermissions([]);
              }}
            >
              {t('cancel')}
            </Button>
            <Button
              onClick={handleBulkAssignPermissions}
              disabled={
                selectedPermissions.length === 0 || bulkAssignMutation.isPending
              }
            >
              {bulkAssignMutation.isPending && (
                <Loader2 className='me-2 h-4 w-4 animate-spin' />
              )}
              {t('bulkAssignPermissions')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
